import { findTranscriptSegmentIndexForTime, type TranscriptSegment } from './transcript';

/** Seconds of recording to start before the snapped segment so the listener hears the lead-in. */
export const LEAD_IN_SECONDS = 4;

/** (heardAtMs - callStartedMs) / 1000, clamped >= 0; null if inputs invalid. (fallback path) */
export function heardOffsetSeconds(
  heardAtMs: number,
  callStartedMs: number | undefined,
): number | null {
  if (!Number.isFinite(heardAtMs) || callStartedMs == null || !Number.isFinite(callStartedMs)) {
    return null;
  }
  return Math.max(0, (heardAtMs - callStartedMs) / 1000);
}

/**
 * Snap an offset to the nearest transcript segment; returns the segment range and a
 * seek target = max(0, segment.start - leadIn). null when there is no offset/segments.
 */
export function snapToTranscriptSegment(
  offsetSeconds: number | null,
  segments: TranscriptSegment[],
  leadInSeconds: number,
): { seekTo: number; range: { start: number; end: number } } | null {
  if (offsetSeconds == null || !Number.isFinite(offsetSeconds) || segments.length === 0) {
    return null;
  }
  const idx = findTranscriptSegmentIndexForTime(segments, offsetSeconds);
  if (idx < 0) return null;
  const seg = segments[idx];
  return {
    seekTo: Math.max(0, seg.start - leadInSeconds),
    range: { start: seg.start, end: seg.end },
  };
}

/**
 * Resolve a marker's seek offset (seconds). Prefers the skew-resilient
 * heard_offset_ms; falls back to (heard_at - callStartedMs). null when neither works.
 */
export function resolveHeardOffsetSeconds(
  marker: { heard_at?: string; heard_offset_ms?: number | string },
  callStartedMs: number | undefined,
): number | null {
  // protojson serializes proto int64 as a JSON string, so heard_offset_ms arrives
  // as e.g. "134000" off the wire. Coerce — Number.isFinite does NOT coerce strings.
  if (marker.heard_offset_ms != null) {
    const ms = Number(marker.heard_offset_ms);
    if (Number.isFinite(ms)) return Math.max(0, ms / 1000);
  }
  const heardAtMs = marker.heard_at ? Date.parse(marker.heard_at) : NaN;
  return heardOffsetSeconds(heardAtMs, callStartedMs);
}
